import {DropboxResponseError} from '@dropbox/api-v2-client';
import {Button} from '@dropbox/dig-components/buttons';
import {Box, Split} from '@dropbox/dig-foundations';
import {useQueryClient} from '@tanstack/react-query';
import {DropboxClientContext} from 'App';
import {t} from 'i18next';
import {useCallback, useContext, useEffect, useRef, useState} from 'react';

import {appsQueryKey} from './api';
import styles from './Apps.module.css';
import {
  AppPublishAttempt,
  AppPublishError,
  AppPublishProgress,
  AppPublishResult,
  ExistingSitePublishTarget,
  prepareAppPublishAttempt,
  publishApp,
} from './publishApp';
import {PreparedAppUpload} from './uploadValidation';

export type PublishStatus = 'idle' | 'preparing' | 'publishing' | 'success' | 'error';

const apiErrorTag = (error: unknown) => {
  const cause = error instanceof AppPublishError ? error.cause : error;
  if (cause instanceof DropboxResponseError) {
    const responseError = cause.error as {'.tag'?: string};
    return responseError?.['.tag'];
  }
  return undefined;
};

const publishErrorMessage = (error: unknown) => {
  switch (apiErrorTag(error)) {
    case 'route_occupied':
      return t('apps_upload_publish_error_route_occupied');
    case 'unauthorized':
      return t('apps_upload_publish_error_unauthorized');
    case 'invalid_request':
    case 'conflict':
    case 'invalid_upload':
    case 'missing_asset':
    case 'stale_candidate':
    case 'expired':
      return t('apps_upload_publish_error_invalid');
    case 'dependency_unavailable':
      return t('apps_upload_publish_error_unavailable');
    default:
      return t('apps_upload_publish_error_generic');
  }
};

const NON_RETRYABLE_ERROR_TAGS = new Set([
  'conflict',
  'expired',
  'invalid_request',
  'invalid_upload',
  'missing_asset',
  'route_occupied',
  'stale_candidate',
  'unauthorized',
]);

const isRetryablePublishError = (error: unknown) => {
  const tag = apiErrorTag(error);
  return !tag || !NON_RETRYABLE_ERROR_TAGS.has(tag);
};

const isAbortError = (error: unknown) => {
  const cause = error instanceof AppPublishError ? error.cause : error;
  return cause instanceof DOMException && cause.name === 'AbortError';
};

export const getAppPublishProgressLabel = (
  status: PublishStatus,
  progress: AppPublishProgress | null
) => {
  if (status === 'preparing') {
    return t('apps_upload_preparing_description');
  }
  if (progress?.phase === 'uploading') {
    return t('apps_upload_progress_description', {
      completed: progress.completed,
      total: progress.total,
    });
  }
  return t('apps_upload_creating_site_description');
};

export type AppPublisherController = {
  canCancel: boolean;
  canRetry: boolean;
  error: string | null;
  isPublishing: boolean;
  progress: AppPublishProgress | null;
  publish: () => Promise<void>;
  cancel: () => void;
  reset: () => void;
  result: AppPublishResult | null;
  status: PublishStatus;
};

export const useAppPublisher = (
  upload: PreparedAppUpload | null,
  existingSite?: ExistingSitePublishTarget
): AppPublisherController => {
  const client = useContext(DropboxClientContext);
  const queryClient = useQueryClient();
  const attemptRef = useRef<AppPublishAttempt | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const canAbortRef = useRef(true);
  const operationSequenceRef = useRef(0);
  const activeOperationRef = useRef<number | null>(null);
  const [status, setStatus] = useState<PublishStatus>('idle');
  const [progress, setProgress] = useState<AppPublishProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canRetry, setCanRetry] = useState(true);
  const [result, setResult] = useState<AppPublishResult | null>(null);

  const isPublishing = status === 'preparing' || status === 'publishing';
  const canCancel = status === 'preparing' || progress?.phase === 'uploading' || !progress;

  useEffect(
    () => () => {
      activeOperationRef.current = null;
      if (canAbortRef.current) {
        abortControllerRef.current?.abort();
      }
    },
    []
  );

  const reset = useCallback(() => {
    activeOperationRef.current = null;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    canAbortRef.current = true;
    attemptRef.current = null;
    setStatus('idle');
    setProgress(null);
    setError(null);
    setCanRetry(true);
    setResult(null);
  }, []);

  const cancel = useCallback(() => {
    if (!canAbortRef.current) {
      return;
    }
    activeOperationRef.current = null;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setStatus('idle');
    setProgress(null);
    setError(null);
  }, []);

  const publish = useCallback(async () => {
    if (!client || !upload || activeOperationRef.current !== null) {
      return;
    }

    const operation = ++operationSequenceRef.current;
    activeOperationRef.current = operation;
    const isCurrentOperation = () => activeOperationRef.current === operation;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    canAbortRef.current = true;
    setError(null);
    setCanRetry(true);
    setResult(null);

    try {
      let attempt = attemptRef.current;
      if (!attempt) {
        setStatus('preparing');
        attempt = await prepareAppPublishAttempt(upload, undefined, abortController.signal);
        if (!isCurrentOperation()) {
          return;
        }
        attemptRef.current = attempt;
      }

      setStatus('publishing');
      const publication = await publishApp(client, attempt, {
        existingSite,
        signal: abortController.signal,
        onProgress: (nextProgress) => {
          if (!isCurrentOperation()) {
            return;
          }
          if (nextProgress.phase === 'creating_site' || nextProgress.phase === 'publishing') {
            canAbortRef.current = false;
          }
          setProgress(nextProgress);
        },
      });
      if (!isCurrentOperation()) {
        return;
      }
      setResult(publication);
      setStatus('success');
      void queryClient.invalidateQueries({queryKey: appsQueryKey});
    } catch (publishError) {
      if (!isCurrentOperation()) {
        return;
      }
      if (isAbortError(publishError)) {
        setStatus('idle');
        setProgress(null);
      } else {
        setError(publishErrorMessage(publishError));
        setCanRetry(isRetryablePublishError(publishError));
        setStatus('error');
      }
    } finally {
      if (isCurrentOperation()) {
        activeOperationRef.current = null;
        abortControllerRef.current = null;
      }
    }
  }, [client, existingSite, queryClient, upload]);

  return {
    canCancel,
    canRetry,
    cancel,
    error,
    isPublishing,
    progress,
    publish,
    reset,
    result,
    status,
  };
};

export const AppPublishActions = ({
  hasValidation,
  publisher,
  readyToPublish,
  showPublish = true,
}: {
  hasValidation: boolean;
  publisher: AppPublisherController;
  readyToPublish: boolean;
  showPublish?: boolean;
}) => {
  if (!hasValidation || publisher.isPublishing || publisher.status === 'success') {
    return null;
  }

  return (
    <Box marginTop="16" display="flex" justifyContent="flex-end">
      <Split gap="12" alignY="center" className={styles.stackedOnMobile}>
        {showPublish && (publisher.status !== 'error' || publisher.canRetry) ? (
          <Split.Item>
            <Button
              variant="primary"
              disabled={!readyToPublish}
              onClick={() => void publisher.publish()}
            >
              {publisher.status === 'error' ? t('try_again') : t('apps_upload_publish')}
            </Button>
          </Split.Item>
        ) : null}
      </Split>
    </Box>
  );
};
